import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import PageHero from '../components/layout/PageHero';
import { MEMBER_GROUPS, normalizeMemberGroup } from '../constants/memberGroups';
import MediaImage from '../components/MediaImage';

type Member = {
  _id: string;
  name: string;
  title?: string;
  group?: string;
  email?: string;
  avatarUrl?: string;
  researchDirections?: string[];
  order?: number;
};

const DEFAULT_INTRO =
  'Our team brings together faculty and students from applied physics, materials science, device engineering, and computational modeling, with a strong emphasis on interdisciplinary collaboration.';

const Members: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [intro, setIntro] = useState(DEFAULT_INTRO);
  const [activeGroup, setActiveGroup] = useState<string>('PI');
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const res = await api.get<Member[]>('/members');
        setMembers(res.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, []);

  useEffect(() => {
    api.get<{ members: string }>('/page-intros').then((res) => {
      const v = res.data?.members?.trim();
      if (v) setIntro(v);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const raw = searchParams.get('group');
    const group = raw === 'Alumni' ? 'Graduated' : raw;
    if (group && MEMBER_GROUPS.some((g) => g.key === group)) {
      setActiveGroup(group);
    }
  }, [searchParams]);

  return (
    <section className="bg-surface text-on-surface min-h-full">
      <PageHero title="Team Members" intro={intro} sectionLabel="People" />

      <div className="mx-auto flex w-full max-w-screen-2xl flex-col gap-12 px-8 pb-20 pt-12 lg:flex-row">
        <aside className="hidden lg:block lg:w-72 lg:shrink-0">
          <div className="rounded-md bg-surface-container-low p-6">
            <h2 className="font-serif text-2xl font-bold text-primary mb-1">Member Directory</h2>
            <p className="text-[11px] uppercase tracking-widest text-on-surface-variant mb-5">
              Groups
            </p>
            <nav className="space-y-1">
              {MEMBER_GROUPS.map((group) => (
                <button
                  key={group.key}
                  onClick={() => setActiveGroup(group.key)}
                  className={`w-full rounded-r-full px-4 py-3 text-left text-sm transition-all ${
                    activeGroup === group.key
                      ? 'bg-surface-container-lowest text-primary font-semibold'
                      : 'text-on-surface-variant hover:text-primary hover:translate-x-1'
                  }`}
                >
                  {group.label}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        <main className="flex-1">
          <div className="glass-panel um-card-soft mb-10 p-2">
            <input
              className="um-input bg-transparent px-3 py-3"
              placeholder="Filter by name, tag, or research direction..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>

          {loading ? (
            <p className="text-on-surface-variant">Loading members...</p>
          ) : (
            <div className="space-y-14">
              {MEMBER_GROUPS
                .filter((g) => g.key === activeGroup)
                .map((group) => {
                  const q = keyword.trim().toLowerCase();
                  const membersInGroup = members
                    .filter((m) => {
                      if (normalizeMemberGroup(m.group) !== group.key) return false;
                      if (!q) return true;
                      const haystack = [
                        m.name,
                        m.title || '',
                        m.email || '',
                        ...(m.researchDirections || []),
                      ]
                        .join(' ')
                        .toLowerCase();
                      return haystack.includes(q);
                    })
                    .sort(
                      (a, b) =>
                        (a.order ?? 0) - (b.order ?? 0) ||
                        a.name.localeCompare(b.name)
                    );

                  return (
                    <section key={group.key}>
                      <div className="mb-8 border-b border-surface-dim pb-4">
                        <h2 className="text-3xl font-semibold leading-tight text-primary md:text-4xl">{group.label}</h2>
                      </div>
                      {membersInGroup.length === 0 ? (
                        <p className="text-on-surface-variant">No matching members in this group.</p>
                      ) : (
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                          {membersInGroup.map((member) => (
                            <Link
                              key={member._id}
                              to={`/members/${member._id}`}
                              className="um-card group relative overflow-hidden bg-surface-container-lowest p-6 transition-colors hover:bg-surface-container"
                            >
                              <div className="relative z-10 flex flex-col items-center text-center">
                                <div className="mb-4 h-24 w-24 overflow-hidden rounded-full border-2 border-surface-container bg-surface p-1">
                                  {member.avatarUrl ? (
                                    <MediaImage
                                      src={member.avatarUrl}
                                      alt={member.name}
                                      className="h-full w-full rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="flex h-full w-full items-center justify-center rounded-full bg-surface-container text-lg font-semibold text-primary">
                                      {member.name.charAt(0)}
                                    </div>
                                  )}
                                </div>
                                <h3 className="mb-1 font-serif text-2xl font-bold text-on-surface group-hover:text-primary transition-colors">
                                  {member.name}
                                </h3>
                                <p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary-container">
                                  {member.title || group.label}
                                </p>
                                <div className="mb-6 flex flex-wrap justify-center gap-2">
                                  {(member.researchDirections || []).slice(0, 3).map((tag) => (
                                    <span
                                      key={tag}
                                      className="rounded-full bg-secondary-container px-3 py-1 text-xs text-on-secondary-container"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                                <div className="mt-auto flex w-full items-center justify-between border-t border-surface-dim/50 pt-4">
                                  <span className="text-sm text-outline-variant">{member.email || 'No email provided'}</span>
                                  <span className="text-primary text-sm font-medium opacity-0 translate-y-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                                    View profile →
                                  </span>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </section>
                  );
                })}
            </div>
          )}
        </main>
      </div>
    </section>
  );
};

export default Members;

